/* ---------------------------------------------------------------------------
   Which kinds of site entry count as work on which kind of planned activity
   (LC-41).

   A match joins a planned activity to a component: "Pier P1 F-1 pile cap" is
   the work A1350 describes. Before this, ANY accepted entry against that
   component started the activity — so the surveyor setting out the cap's
   corners started the pour, and the variance said the cap began eleven days
   early. The component says where; this says what.

   THE TWO VOCABULARIES. A planned activity carries the modeller's component
   kind — `Pile`, `Bent Cap`, `Deck`, the keys of `DEFAULT_DURATIONS_BY_KIND` in
   vendor/bridge-modeler/src/schedule/Schedule.ts — or, on a programme typed by
   hand, a word of the planner's own (`Excavation`, `Survey`). A site entry
   carries an activity-library id from ./activities.ts. The table below says
   which ids are work of each kind.

   THE RULES THE TABLE KEEPS:

     · Structural concrete — a pile cap, a column, a wall, a cap, a deck — is
       started by its reinforcement, its formwork or its pour, and by nothing
       else. Setting out, a survey check, a hold point, an NCR and a stoppage
       are about the work; they are not the work.
     · Precast girders are started by casting and finished by erecting, both
       under the one kind the modeller gives them (`Girder`); the programme
       separates yard and lift by component, not by kind.
     · A bearing is started by being set. The level survey before it is not.
     · A kind this table does not know — a planner's own word, `Preliminaries`,
       an empty kind — puts no filter on: every entry against the component
       counts, as every entry did before. Refusing an unknown kind would leave
       a hand-typed programme with nothing started at all.

   Case and spacing do not matter (`bent cap`, `Bent Cap`, `BentCap`).
   --------------------------------------------------------------------------- */

const CONCRETE = ['rebar-fixed', 'formwork-erected', 'concrete-poured', 'concrete-cured', 'formwork-struck'] as const;

/**
 * The library ids that are work of each kind. `null` is "any entry counts",
 * written out for the kinds where that is the answer rather than left to the
 * fallback, so a reader can see it was decided.
 */
export const KIND_ACTIVITIES: Readonly<Record<string, readonly string[] | null>> = {
  /* Foundations. */
  Pile: ['pile-bored', 'pile-cage', 'pile-concreted'],
  'Pile Cap': [...CONCRETE, 'pilecap-poured'],
  Footing: [...CONCRETE, 'footing-poured'],
  /* Substructure. */
  Column: CONCRETE,
  'Bent Cap': CONCRETE,
  Wall: CONCRETE,
  'End Wall': CONCRETE,
  'Wing Wall': CONCRETE,
  'Back Wall': CONCRETE,
  Abutment: [...CONCRETE, 'footing-poured'],
  Bearing: ['bearing-set'],
  /* Superstructure. */
  Girder: ['girder-cast', 'girder-stressed', 'post-tensioned', 'girder-erected'],
  'Box Girder': [...CONCRETE, 'girder-cast', 'girder-stressed', 'post-tensioned', 'girder-erected'],
  Diaphragm: [...CONCRETE, 'diaphragm-cast'],
  Deck: [...CONCRETE, 'deck-poured'],
  'Approach Slab': CONCRETE,
  /* Finishes and furniture. */
  'Side Wall': [...CONCRETE, 'parapet-built'],
  Railing: ['parapet-built'],
  'Concrete Railing': [...CONCRETE, 'parapet-built'],
  'Composite Railing': [...CONCRETE, 'parapet-built'],
  'Expansion Joint': ['joint-installed'],
  'Road Surface': ['wearing-course'],
  Scupper: ['drainage-installed'],
  'Drop Pipe': ['drainage-installed'],
  'Collector Pipe': ['drainage-installed'],
  'Down Pipe': ['drainage-installed'],
  Manhole: ['drainage-installed'],
  /* The planner's own words, where a programme was typed rather than extracted. */
  Excavation: ['excavation', 'dewatering'],
  Earthworks: ['excavation', 'backfill'],
  Pavement: ['backfill', 'wearing-course'],
  'River Training': ['excavation', 'backfill'],
  Services: ['drainage-installed'],
  Survey: ['setting-out', 'survey-check'],
  Test: ['pile-test'],
  Preliminaries: null,
  'Lane Divider': null,
  'Lighting Pole': null,
};

function keyOf(kind: string): string {
  return kind.replace(/[\s_-]+/g, '').toLowerCase();
}

const BY_KEY = new Map(Object.entries(KIND_ACTIVITIES).map(([k, v]) => [keyOf(k), v]));

/** The library ids that count as work of a kind, or null where any entry does. */
export function activitiesForKind(kind: string | null | undefined): readonly string[] | null {
  if (!kind) return null;
  return BY_KEY.get(keyOf(kind)) ?? null;
}

/**
 * Whether an entry of `activity` is work on a planned activity of `kind`. An
 * entry with no activity (none is ever stored without one; the tests build
 * them) is not filtered.
 */
export function entryFitsKind(kind: string | null | undefined, activity: string | null | undefined): boolean {
  if (!activity) return true;
  const allowed = activitiesForKind(kind);
  return allowed === null || allowed.includes(activity);
}

/**
 * The library id to prefill the progress dialog with for a planned activity
 * (LC-45): the one of its kind whose words its name uses — "Girders — erect"
 * is `girder-erected`, not `girder-cast` — and otherwise the pour, where the
 * kind has one, because it is what a crew records most. Null for a kind with no
 * list: the person chooses.
 */
export function defaultActivityForKind(kind: string | null | undefined, name = ''): string | null {
  const allowed = activitiesForKind(kind);
  if (allowed === null || allowed.length === 0) return null;
  const words = name.toLowerCase().split(/[^a-z]+/).filter((w) => w.length >= 4);
  const stem = (w: string): string => w.slice(0, 5);
  let best: string | null = null;
  let score = 0;
  for (const id of allowed) {
    const parts = id.split('-').map(stem);
    const hits = parts.filter((p) => words.some((w) => stem(w) === p)).length;
    if (hits > score) {
      best = id;
      score = hits;
    }
  }
  /* A name that only repeats the kind ("Pile cap") says nothing about which of
     its activities is meant. */
  const kindWords = (kind ?? '').toLowerCase().split(/[^a-z]+/).filter(Boolean).map(stem);
  if (best !== null && best.split('-').map(stem).every((p) => kindWords.includes(p) || !words.some((w) => stem(w) === p))) {
    best = null;
  }
  return best ?? [...allowed].reverse().find((a) => /poured|concreted|cast|erected|set|installed|laid|course/.test(a)) ?? allowed[0]!;
}
